import { FS_MASK_CLS } from '@common/constants';
import { StyledCreditsLimitBanner } from '@common/credits/ProfessionalEdition/CreditsLimitBanner';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { UserTypes } from '@common/UserTypes';
import { WorkspaceInfo } from '@pages/Metadata/components/workspaces/WorkspaceInfo';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { theme } from '@prophecy/theme';
import { Stack, TabPane, Tabs } from '@prophecy/ui-v3';
import { MetadataPageContainer } from '@prophecy/ui-v3/Layout';
import {
  StyledTabsContent,
  StyledTabsList,
  StyledTabsTrigger,
  StyledTabsTriggerGroup,
  StyledTriggerGroupLabel
} from '@prophecy/ui-v3/Tabs/styled';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useAppMetadata } from '../../context/appMetadata';
import { useGlobalVariables } from '../../context/globalConfig/securedConfigHook';
import { useUnsecuredGlobalVariables } from '../../context/globalConfig/unsecuredConfigHook';
import { useMixPanel } from '../../context/mixpanel/context';
import { useUser } from '../../context/user';
import AdminSettings from './Admin/AdminSettings';
import { Billing } from './Billing';
import { AccessTokens } from './components/AccessTokens';
import { GitTab } from './components/GitTab';
import { Info } from './components/Info';
import { SetupEnvironment } from './components/SetupEnvironment/SetupEnvironment';
import { WorkspaceFabricTabs } from './components/WorkspaceFabricTabs';
import { AccessTokenProvider } from './contexts/accessTokensContext';
import { WorkspaceFabricProvider } from './contexts/WorkspaceFabricContext/WorkspaceFabricProvider';

const VERTICAL_SETTINGS_LAYOUT = [
  {
    groupLabel: 'Personal',
    tabs: [
      { title: 'About Me', entity: 'aboutMe' },
      { title: 'Developer', entity: 'developer' }
    ]
  },
  {
    groupLabel: 'Workspace',
    tabs: [
      { title: 'Info', entity: 'info' },
      { title: 'Billing', entity: 'billing' },
      { title: 'Connections', entity: 'connections' },
      { title: 'Secrets', entity: 'secrets' },
      { title: 'Access', entity: 'access' },
      { title: 'Admin', entity: 'admin' }
    ]
  }
] as const;

const DEFAULT_VERTICAL_SETTINGS_TAB = 'aboutMe';
const ALL_VERTICAL_TAB_ENTITIES: string[] = VERTICAL_SETTINGS_LAYOUT.flatMap(({ tabs }) =>
  tabs.map(({ entity }) => entity)
);

const TAB_TITLE_BY_ENTITY = VERTICAL_SETTINGS_LAYOUT.reduce<Record<string, string>>((acc, group) => {
  group.tabs.forEach(({ entity, title }) => {
    acc[entity] = title;
  });
  return acc;
}, {});

const StyledVerticalSettingsTabs = styled(Tabs)`
  width: 100%;
  height: 100%;
  ${StyledTabsList} {
    width: 220px;
    height: auto;
  }

  ${StyledTabsTriggerGroup} {
    display: flex;
    flex-direction: column;
    align-items: stretch;
  }

  ${StyledTriggerGroupLabel} {
    padding: ${theme.spaces.x12} ${theme.spaces.x12} ${theme.spaces.x8};
  }

  ${StyledTabsTrigger} {
    width: 100%;
    height: auto;
    padding: ${theme.spaces.x8} ${theme.spaces.x12} ${theme.spaces.x8} ${theme.spaces.x24};
    margin: 0;
  }

  ${StyledTabsContent} {
    padding-top: 0;
    padding-left: ${theme.spaces.x16};
  }
`;

const SETUP_ENVIRONMENT_ACTION = 'environment';

export default function VerticalAppUserSettings() {
  const { tab } = useParams<ExtractUrlPathVariables<typeof Private_Routes.Settings.tab.url>>();
  const user = useUser();
  const globalConf = useGlobalVariables();
  const userInfo = useAppMetadata()?.user;
  const isAdmin = userInfo?.type === UserTypes.ClusterAdmin || userInfo?.type === UserTypes.Support;
  const navigate = useNavigate();
  const unsecureConfig = useUnsecuredGlobalVariables();
  const isSSOEnabled =
    (unsecureConfig.isConfigurableAuthEnabled &&
      ((globalConf.getCloudProvider as string)?.toLowerCase() as string) === 'saas') ||
    isAdmin;
  const hideAccessTab = !isSSOEnabled;
  const isTabHidden = (entity: string) => (entity === 'access' && hideAccessTab) || (entity === 'admin' && !isAdmin);
  const getTabDisabledMessage = (entity: string) =>
    entity === 'access' ? 'To use this feature, upgrade to Prophecy Enterprise Edition' : '';
  const availableTabs = ALL_VERTICAL_TAB_ENTITIES.filter((entity) => !isTabHidden(entity));
  const defaultTab = availableTabs[0] || DEFAULT_VERTICAL_SETTINGS_TAB;
  const resolvedTab = tab && availableTabs.includes(tab) ? tab : defaultTab;

  const [activeTab, setActiveTab] = useState<string>(() => resolvedTab);

  const { track } = useMixPanel();

  useEffect(() => {
    track?.(EntityEvents[Entity.Team].metadata.getTabChange(activeTab));
  }, [track, activeTab]);

  useEffect(() => {
    setActiveTab(resolvedTab);
  }, [resolvedTab]);

  const onChange = (currentTab: string) => {
    if (isTabHidden(currentTab)) {
      return;
    }
    setActiveTab(currentTab);
    navigate(Private_Routes.Settings.tab.getUrl({ tab: currentTab }));
    track(EntityEvents.settings.home.tabChange, {
      name: user?.user?.firstName,
      tab: currentTab,
      url: window.location.href
    });
  };

  const isSupportUser = userInfo?.type === UserTypes.Support;

  const getTabDetail = useCallback(
    (entity: string) => {
      switch (entity) {
        case 'aboutMe':
          return <Info />;
        case 'developer':
          return <GitTab />;
        case 'info':
          return <WorkspaceInfo />;
        case 'billing':
          return <Billing />;
        case 'connections':
          return <WorkspaceFabricTabs tab='connections' />;
        case 'secrets':
          return <WorkspaceFabricTabs tab='secrets' />;
        case 'access':
          return (
            <AccessTokenProvider>
              <AccessTokens />
            </AccessTokenProvider>
          );
        case 'admin':
          return <AdminSettings isSupportUser={isSupportUser} />;
        default:
          return null;
      }
    },
    [isSupportUser]
  );

  const [searchParams] = useSearchParams();
  const isSetupEnvironmentOpen = searchParams.get('action') === SETUP_ENVIRONMENT_ACTION;

  if (isSetupEnvironmentOpen) {
    return (
      <Stack width='100%' height='100%' direction='vertical'>
        <StyledCreditsLimitBanner />
        <WorkspaceFabricProvider type='setup'>
          <SetupEnvironment />
        </WorkspaceFabricProvider>
      </Stack>
    );
  }

  const UserSettings = (
    <StyledVerticalSettingsContainer className={FS_MASK_CLS} gap={theme.spaces.x12} align='center' alignY='center'>
      <DocumentTitle title={`Settings: ${TAB_TITLE_BY_ENTITY[activeTab] || 'Workspace'}`} />
      <WorkspaceFabricProvider type='management'>
        <StyledVerticalSettingsTabs
          activeKey={activeTab}
          onChange={onChange}
          orientation='vertical'
          variant='underline'
          size='s'>
          {VERTICAL_SETTINGS_LAYOUT.map(({ groupLabel, tabs }) => (
            <Tabs.TabPaneGroup key={groupLabel} label={groupLabel}>
              {tabs.reduce<JSX.Element[]>((currentTabs, { title, entity }) => {
                if (isTabHidden(entity)) {
                  return currentTabs;
                }

                const disabledMessage = getTabDisabledMessage(entity);
                currentTabs.push(
                  <TabPane key={entity} tab={title} disabled={Boolean(disabledMessage)} tooltip={disabledMessage}>
                    {getTabDetail(entity)}
                  </TabPane>
                );

                return currentTabs;
              }, [])}
            </Tabs.TabPaneGroup>
          ))}
        </StyledVerticalSettingsTabs>
      </WorkspaceFabricProvider>
    </StyledVerticalSettingsContainer>
  );
  return (
    <Stack width='100%' height='100%' direction='vertical'>
      <StyledCreditsLimitBanner />
      <MetadataPageContainer main={UserSettings} hasLayoutContainer={false}></MetadataPageContainer>
    </Stack>
  );
}

const StyledVerticalSettingsContainer = styled(Stack)`
  width: 100%;
  height: 100%;
  overflow-x: auto;
  cursor: default;
  padding: ${theme.spaces.x40} ${theme.spaces.x32} ${theme.spaces.x32};
`;
